import {
  Box,
  chakra,
  Flex,
  Grid,
  GridItem,
  Image,
  shouldForwardProp,
  Text,
  useBreakpointValue
} from '@chakra-ui/react';
import { isValidMotionProp, motion } from 'framer-motion';
import i18next from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { gameListFromBottom } from '../../../animateConstant';
import { COMMON_WEB_PATH } from '../../../constant';
import useCheckLogin from '../../../hook/useCheckLogin';
import useDeviceType from '../../../hook/useDeviceType';
import { languageTransfer } from '../../../utils/languageTransfer';
import GameItem from '../../GameItem/GameItem';
import LoadingBlock from '../../Loading/LoadingBlock';
import NotFound from '../../ProgressBlock/NotFound';
import { routeMapping } from '../GameTypeSelector/utils/routeMapping';
import SubListSelector from '../SubListSelector/SubListSelector';

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop)
});

const GameSelector = ({
  subListSelector,
  hiddenTitle,
  gameMainTypeFromProps,
  customSubType,
  customGamePlatform,
  sliceCount,
  isNew,
  isHot,
  isHighlight,
  onGameHover, // 遊戲hover
  topLayout,
  customTitleIcon, // 自定義titleIcon
  showSupplierButton, // 是否顯示供應商按鈕
  specialBorder, //是否用特殊GAMEITEM編筐
  longImage, // 是否用長圖
  isGridXLayout
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);

  const scrollRef = useRef();

  const [displayedItems, setDisplayedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightUid, setHighlightUid] = useState(null);

  const { gameMainType, gamePlatform, subType } = useParams();
  const { isMachineUser } = useCheckLogin();

  const custom_subType = useMemo(() => {
    return customSubType || subType;
  }, [customSubType, subType]);

  const custom_gameplatform = useMemo(() => {
    return customGamePlatform || gamePlatform;
  }, [customGamePlatform, gamePlatform]);
  const isMobile = useDeviceType() === 'Mobile';
  const isPC = useDeviceType() === 'PC';

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const {
    sortedByPlatformGames,
    sortedByPlatformAndCategoryGames,
    gameList,
    gameListByTypeAndSubType,
    allGames,
    favoriteGameList,
    randomlySelectedGames,
    newGameList,
    topGameList,
    allGameType,
    exclusiveBrandGames,
    highlightGames
  } = isHandleAllGame || {};

  const platformGamesList = useMemo(() => {
    const mainType = gameMainTypeFromProps || gameMainType;
    const current_game_type = routeMapping(mainType)?.origin;

    const gameTypeCode = allGameType?.find(
      (t) => t.gameTypeName === current_game_type
    )?.gameTypeUid;

    // 特別處理customSubType="topGameList"的情況 顯示全部topGameList
    if (customSubType === 'topGameList') {
      return topGameList || []; // 直接返回全部topGameList
    }

    // 特別處理customSubType="New" 的情況
    if (customSubType === 'New') {
      return newGameList || []; // 直接返回 newGameList 只返回前8個 不需要其他過濾
    }

    // 特別處理customSubType="exclusiveBrandGames"的情況 顯示全部獨家遊戲
    if (customSubType === 'exclusiveBrandGames') {
      return exclusiveBrandGames || []; // 直接返回全部獨家遊戲
    }

    // 特別處理customSubType="Popular"的情況 從highlightGames取前40個
    if (customSubType === 'Popular') {
      const gamesArray = Object.values(highlightGames || {});

      // 根據 game_tags 排序
      const sortedGames = gamesArray.sort((a, b) => {
        const aHotTag = a.game_tags?.find((tag) => tag.startsWith('hot_'));
        const bHotTag = b.game_tags?.find((tag) => tag.startsWith('hot_'));

        // 如果沒有 hot tag，排到最後面
        if (!aHotTag) return 1;
        if (!bHotTag) return -1;

        const aOrder = parseInt(aHotTag.split('_')[1]);
        const bOrder = parseInt(bHotTag.split('_')[1]);

        return aOrder - bOrder;
      });

      // 過濾掉沒有 hot tag 的遊戲並返回前40個
      return sortedGames.slice(0, 40);
    }

    if (custom_subType) {
      const determineIsAllPlatform = (gameList) => {
        if (custom_gameplatform === 'all-platform') {
          return (
            gameList?.filter((game) => game.gameTypeUid === gameTypeCode) || []
          );
        } else {
          return (
            gameList
              ?.filter((game) => game.gameTypeUid === gameTypeCode)
              ?.filter(
                (game) =>
                  game.game_platform === custom_gameplatform ||
                  game.SubPlatformEnum === custom_gameplatform
              ) || []
          );
        }
      };
      switch (custom_subType) {
        case 'AllGames':
          return determineIsAllPlatform(allGames);
        case 'MyFavorite':
          return determineIsAllPlatform(favoriteGameList);
        case 'ClickedGames':
          return determineIsAllPlatform(randomlySelectedGames);
        case 'News':
          return determineIsAllPlatform(newGameList);
        case 'Top':
          return determineIsAllPlatform(topGameList);
        default:
          if (custom_gameplatform === 'all-platform') {
            return (
              gameListByTypeAndSubType?.[current_game_type]?.[
                custom_subType
              ]?.filter((game) => game.gameTypeUid === gameTypeCode) || []
            );
          } else {
            return (
              sortedByPlatformAndCategoryGames?.[custom_gameplatform]?.[
                custom_subType
              ] || []
            );
          }
      }
    }
    if (gameList && mainType && custom_gameplatform) {
      return gameList?.[current_game_type]?.[custom_gameplatform] || [];
    } else {
      return Object?.values(gameListByTypeAndSubType?.[current_game_type] || [])
        ?.flat()
        ?.filter((game) => game.gameTypeUid === gameTypeCode)
        ?.slice(0, 150);
    }
  }, [
    gameList,
    custom_gameplatform,
    gameMainType,
    custom_subType,
    sortedByPlatformAndCategoryGames,
    gameListByTypeAndSubType,
    gameMainTypeFromProps,
    allGameType,
    highlightGames
  ]);

  const ITEMS_PER_PAGE = useBreakpointValue({
    base: 12,
    md: 25,
    lg: 35,
    xl: 32
  });

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isLoading]);

  useEffect(() => {
    setCurrentPage(1);
    // setIsLoading(true);
    setDisplayedItems(platformGamesList?.slice(0, ITEMS_PER_PAGE));
  }, [
    platformGamesList,
    custom_gameplatform,
    subType,
    gameMainType,
    custom_subType
  ]);

  // useEffect(() => {
  //   if (scrollRef.current) {
  //     if (subType || custom_gameplatform) {
  //       const elementPosition = scrollRef.current.getBoundingClientRect().top;
  //       const offsetPosition = elementPosition + window.pageYOffset - 55;

  //       window.scrollTo({
  //         top: offsetPosition,
  //         behavior: "smooth",
  //       });
  //     }
  //   }
  // }, [subType, custom_gameplatform]);

  const gameListSlice = useMemo(() => {
    if (sliceCount) {
      return platformGamesList
        ?.sort((a, b) => b.gameHighlight - a.gameHighlight)
        ?.slice(0, sliceCount);
    } else {
      return platformGamesList?.sort(
        (a, b) => b.gameHighlight - a.gameHighlight
      );
    }
  }, [sliceCount, platformGamesList]);

  useEffect(() => {
    const savedUid = sessionStorage.getItem('lastClickedGameUid');

    if (gameListSlice?.length > 0) {
      if (savedUid) {
        setHighlightUid(savedUid);

        const timer = setTimeout(() => {
          const targetElement = document.getElementById(
            `game-item-${savedUid}`
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: 'auto',
              block: 'center'
            });
          }
          sessionStorage.removeItem('lastClickedGameUid');
        }, 300);

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [gameListSlice]);

  return (
    <Box
      ref={scrollRef}
      // borderTopRadius={`common`}
      // borderTop="3px solid"
      // borderColor={`brand.400`}
      // bgGrdi="linear(to-b, transparent, dark.500 400%)"
      // px="common-y"

      px={{
        base: '0px',
        md: '0px'
      }}
      py={{
        base: '0px',
        md: '0px'
      }}
      my={{
        base: '5px',
        md: '0px'
      }}
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize={{
        base: 'cover',
        sm: gameListSlice?.length > 10 ? 'cover' : '100% 100%'
      }}
      borderRadius={'15px'}
      position="relative"
      minH={
        isGridXLayout
          ? {
              base: 'unset',
              md: '300px',
              lg: '533px'
            }
          : {
              base: '270px'
            }
      }
    >
      {hiddenTitle ? null : (
        <Flex alignItems={'center'} justifyContent={'space-between'} w="100%">
          <Box
            position="relative"
            w={{
              base: '200px',
              lg: '300px'
            }}
            // display={{
            //   base: "none",
            //   xl: "block",
            // }}
          >
            <Image
              src={`${COMMON_WEB_PATH}/now888/title${
                i18next.language == 'tw' ? '' : `/${i18next.language}`
              }/${customTitleIcon || gameMainTypeFromProps}_title_.png`}
              w={{
                base: '60%',
                md: '100%'
              }}
              onError={(e) => {
                e.target.src = `${COMMON_WEB_PATH}/now888/title/${
                  customTitleIcon || gameMainTypeFromProps
                }_title_.png`;
                e.target.onerror = null; // Prevent infinite loop
              }}
              objectFit="contain"
            />
          </Box>
          {showSupplierButton && (
            <Link to="casino-group">
              <Flex
                alignItems={`center`}
                justifyContent={`space-between`}
                gap="10px"
                //borderRadius={`10px`}
                width={`100px`}
                height="39px"
                display={{
                  base: 'flex',
                  md: 'none'
                }}
                // bg={searchBg}
                //bgImage={`url('${COMMON_WEB_PATH}/FlyDragon/game_BG.webp')`}
                bgImage={{
                  base: `url('${COMMON_WEB_PATH}/5285/full_platform_button_blue.png')`,
                  md: `url('${COMMON_WEB_PATH}/FlyDragon/game_BG.webp')`
                }}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100%100%"
                px="10px"
                pl="30px"
                // py="5px"
                // shadow={`0px 1px 5px #9D9D9D`}
              >
                <Text
                  sx={{
                    fontFamily: "'Noto Sans TC', sans-serif"
                  }}
                  fontWeight={600}
                  as="span"
                  className="text-overflow mb-1 text-xs"
                >
                  {gamePlatform && gamePlatform !== 'all-platform'
                    ? gamePlatform
                    : i18n('All providers')}
                </Text>

                {/* <Icon
              as={RiMenuSearchFill}
              fontSize="2xl"
              color={searchText}
              className="mb-1"
            /> */}
              </Flex>
            </Link>
          )}
        </Flex>
      )}
      {subListSelector && (
        <Box mb="10px">
          <SubListSelector
            gameMainTypeFromProps={gameMainTypeFromProps}
            forceShowAll={
              !custom_gameplatform || custom_gameplatform == 'all-platform'
            }
          />
        </Box>
      )}

      <LoadingBlock size="50px" commonLoading={isLoading}>
        {displayedItems?.length === 0 && <NotFound />}
        <ChakraBox
          {...gameListFromBottom}
          {...(isGridXLayout
            ? {
                overflowX: 'scroll',
                whiteSpace: 'nowrap'
              }
            : {})}
        >
          <Grid
            gridAutoFlow={'row'}
            gridTemplateRows={
              topLayout || isGridXLayout ? 'repeat(2,minmax(0,1fr))' : ''
            }
            minWidth="max-content"
            templateColumns={
              isGridXLayout
                ? {
                    base: `repeat(${Math.ceil(
                      2 + (gameListSlice.length - 2) / 2
                    )}, 135px)`,
                    md: `repeat(${Math.ceil(
                      2 + (gameListSlice.length - 2) / 2
                    )}, 220px)`,
                    lg: `repeat(${Math.ceil(
                      2 + (gameListSlice.length - 2) / 2
                    )}, 300px)`
                  }
                : {
                    base: 'repeat(2, minmax(0, 1fr))', // 0-480px: 3列
                    sm: 'repeat(2, minmax(0, 1fr))', // 480px+: 3列
                    md: 'repeat(4, minmax(0, 1fr))', // 768px+: 4列
                    lg: 'repeat(4, minmax(0, 1fr))', // 992px+: 5列
                    xl: 'repeat(5, minmax(0, 1fr))', // 1280px+: 5列
                    '2xl': 'repeat(5, minmax(0, 1fr))' // 1536px+: 6列
                  }
            }
            sx={{
              '@media screen and (min-width: 1800px)': {
                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' // 1800px+: 7列
              },
              '@media screen and (min-width: 2200px)': {
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' // 2200px+: 7列
              },
              '@media screen and (min-width: 2560px)': {
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' // 2K+: 8列
              },
              '@media screen and (min-width: 3840px)': {
                gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' // 4K+: 10列
              }
            }}
            rowGap={{
              base: isGridXLayout ? '12px' : '12px',
              md: isGridXLayout ? '15px' : '15px'
            }}
            columnGap={{
              base: '12px',
              md: '15px'
            }}
            mt={{
              base: '10px',
              xl: '10px' // xl以上時 如果gameMainTypeFromProps有值，則mt為-60px，否則為10px
            }}
          >
            {gameListSlice?.map((item, index) => {
              const gameName =
                item?.[
                  `gameName${languageTransfer({
                    lang: i18next.language
                  })}`
                ];
              const gameNameEn = item?.gameNameEn;
              const platform = item?.game_platform;
              const gameSubType = item?.game_subtype;

              const gameCode = item?.gameCode;
              const gameType = item?.gameType;
              const gameUid = item?.gameUid;

              const isHighlighted = highlightUid == gameUid;

              return (
                <GridItem
                  key={gameUid || index}
                  id={`game-item-${gameUid}`}
                  colSpan={1}
                  rowSpan={1}
                  pb="2.5px"
                  // bg="brand.900"
                  // borderRadius={"15px"}
                  mt="-10px"
                  position="relative"
                  onMouseEnter={() => {
                    onGameHover?.(item);
                    if (isHighlighted) setHighlightUid(null);
                  }}
                  onClick={() => {
                    sessionStorage.setItem('lastClickedGameUid', gameUid);
                  }}
                >
                  <GameItem
                    gameProps={{
                      gameName,
                      platform,
                      gameCode,
                      gameType,
                      gameNameEn,
                      gameSubType
                    }}
                    longImage={longImage}
                    originProps={item}
                    itemClass={`!h-[130px]`}
                    itemIndex={index}
                    isNew={isNew}
                    isHot={isHot}
                    isHighlight={isHighlight}
                    specialBorder={specialBorder}
                    recentedPlayedComponent={
                      isHighlighted && (
                        <Flex
                          position="absolute"
                          top="0"
                          left="0"
                          right="0"
                          bottom="0"
                          bg="rgba(0,0,0,0.6)"
                          zIndex="10"
                          alignItems="center"
                          justifyContent="center"
                          pointerEvents="none"
                          border="2px solid"
                          borderColor="brand.400"
                        >
                          <Text
                            color="white"
                            fontSize="14px"
                            fontWeight="700"
                            textShadow="0px 0px 4px rgba(0,0,0,0.8)"
                          >
                            {i18n('lastPlayed') || 'LAST VIEWED'}
                          </Text>
                        </Flex>
                      )
                    }
                  />
                </GridItem>
              );
            })}
          </Grid>
        </ChakraBox>
      </LoadingBlock>
      {/* </InfiniteScroll> */}
    </Box>
  );
};

export default GameSelector;
